import { createContext, useContext, useState, useEffect } from "react";
import { post, get } from "../../http/api";
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import countriesData from "./data/countries.json";
import { getCountryCallingCode, isValidPhoneNumber } from "libphonenumber-js";


const RegisterContext = createContext();

export const RegisterProvider = ({ children }) => {
    const [step, setStep] = useState(1);
    const [profile, setProfile] = useState("");
    const [formData, setFormData] = useState({ language: "fr" });
    const [details, setDetails] = useState({});
    const [verificationData, setVerificationData] = useState({});
    const [verificationFilesError, setVerificationFilesError] = useState(false);

    const { t } = useTranslation();



    const [fieldErrors, setFieldErrors] = useState({
        username: "",
        email: "",
        birthdate: "",
        phone: "",
        password: "",
    });
    const [passwordRules, setPasswordRules] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    const [passwordStrength, setPasswordStrength] =
        useState({
            score: 0,
            message: "",
            valid: false,
        });

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [sport, setSport] = useState("");
    const [countries] = useState(countriesData);
    // const [countries] = useState(["Spain", "Morocco", "Italy", "Japan", "Portugal"]);
    // const [country, setCountry] = useState("");
    const [footballCountry, setFootballCountry] = useState("");
    const [leagues, setLeagues] = useState([]);
    const [teams, setTeams] = useState([]);
    const [league, setLeague] = useState("");
    const [legalForm, setLegalForm] = useState("");
    const [loading, setLoading] = useState(false);
    const [mediaType, setMediaType] = useState("");

    // Countries are objects ({ name, code, flag }), not translation keys. Keeping
    // the formatting here prevents role forms from accidentally calling
    // `toLowerCase()` on an object.
    const getCountryTranslation = (country) => {
        if (country && typeof country === "object") {
            return `${country.flag || ""} ${country.name || ""}`.trim();
        }

        return typeof country === "string" ? country : "";
    };


    const getAge = (birthdate) => {
        const today = new Date();
        const birth = new Date(birthdate);

        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();

        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
            age--;
        }

        return age;
    };


    const validatePassword = (password) => {
        const checks = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[^A-Za-z0-9]/.test(password),
        };

        const score =
            Object.values(checks).filter(Boolean).length;

        const missing = [];

        if (!checks.length) missing.push(t("registerRules.characters"));
        if (!checks.uppercase) missing.push(t("registerRules.uppercase"));
        if (!checks.lowercase) missing.push(t("registerRules.lowercase"));
        if (!checks.number) missing.push(t("registerRules.number"));
        if (!checks.special) missing.push(t("registerRules.special"));

        const valid = score === 5;

        setPasswordStrength({
            score,
            valid,
            message: valid
                ? t("registerRules.strongPassword")
                : `${t("registerRules.missing")} : ${missing.join(", ")}`
        });

        setFieldErrors((prev) => ({
            ...prev,
            password: valid ? "" : t("registerRules.weakPassword"),
        }));
    };


    const next = async () => {
        if (step === 1) {
            setStep(2);
            return;
        }

        if (step === 2) {
            const missingAccountField = ["username", "email", "password", "language"]
                .some((field) => !String(formData[field] || "").trim());

            if (missingAccountField || !passwordStrength.valid) {
                toast.error(t("registerErrors.fixFields"));
                return;
            }

            const hasErrors = Object.values(fieldErrors).some(Boolean);

            if (hasErrors) {
                toast.error(
                    t("registerErrors.fixFields")
                );
                return;
            }

            // validation Lawyer / Agent
            if (
                profile === "lawyer" &&
                (!details.statuses || details.statuses.length === 0)
            ) {
                toast.error(
                    t("registerErrors.selectSpeciality")
                );
                return;
            }

            try {
                const res = await get(
                    `users/check?username=${formData.username}&email=${formData.email}`
                );

                if (res.exists) {
                    setFieldErrors((prev) => ({
                        ...prev,
                        username: res.usernameTaken
                            ? t("registerErrors.usernameUsed")
                            : prev.username,

                        email: res.emailTaken
                            ? t("registerErrors.emailUsed")
                            : prev.email,
                    }));

                    return;
                }

                setStep(3);

            } catch {
                toast.error(
                    t("registerErrors.verificationError")
                );
            }
        }
    };
    const prev = () => setStep(s => s - 1);


    // Email verification
    const [emailVerificationOpen, setEmailVerificationOpen] =
        useState(false);

    const [emailCode, setEmailCode] = useState("");
    const [verificationError, setVerificationError] =
        useState("");

    const [pendingRegister, setPendingRegister] =
        useState(false);



    const PHONE_RULES = {
        Morocco: {
            regex: /^(?:\+212|0)([5-7]\d{8})$/,
            placeholder: "+212600000000",
        },

        Spain: {
            regex: /^(?:\+34)?[6789]\d{8}$/,
            placeholder: "+34600000000",
        },

        Italy: {
            regex: /^(?:\+39)?3\d{8,9}$/,
            placeholder: "+393400000000",
        },

        Japan: {
            regex: /^(?:\+81|0)[789]0\d{8}$/,
            placeholder: "+819012345678",
        },

        Portugal: {
            regex: /^(?:\+351)?9[1236]\d{7}$/,
            placeholder: "+351912345678",
        },
    };



    const handleChange = (e) => {
        const { name, value, files } = e.target;

        let finalValue = files ? files[0] : value;

        // username invalid
        if (name === "username") {

            const usernameRegex = /^[a-zA-Z0-9._]{3,30}$/;

            if (value && !usernameRegex.test(value)) {

                setFieldErrors((prev) => ({
                    ...prev,
                    username: t("registerErrors.invalidUsername"),
                }));

            } else {

                setFieldErrors((prev) => ({
                    ...prev,
                    username: "",
                }));

            }

            // Emojis, spaces and symbols are never inserted in the field.
            finalValue = value.replace(/[^a-zA-Z0-9._]/g, "").slice(0, 30);
        }

        setFormData((prev) => {
            const next = {
                ...prev,
                [name]: finalValue,
            };

            // Every role uses the same country field. When it changes, place
            // the corresponding calling code directly in the phone input.
            if (name === "country") {
                const selectedCountry = countriesData.find(
                    (country) => country.name === value
                );

                try {
                    next.phone = selectedCountry?.code
                        ? `+${getCountryCallingCode(selectedCountry.code)}`
                        : "";
                } catch {
                    next.phone = "";
                }
            }

            return next;
        });

        // reset erreurs username / email
        if (name === "email") {
            setFieldErrors((prev) => ({
                ...prev,
                email: "",
            }));
        }

        if (name === "password") {
            validatePassword(value);
        }

        // validation âge selon profil
        if (name === "birthdate") {
            if (!value) return;

            const age = getAge(value);

            const minAgeByRole = {
                athlete: 13,
                fan: 13,
                staff: 18,
                lawyer: 18,
                media: 18,
                brand: 18,
            };

            // Club => pas de birthdate
            if (profile === "club") {
                setFieldErrors((prev) => ({
                    ...prev,
                    birthdate: "",
                }));
                return;
            }

            const minAge = minAgeByRole[profile] ?? 18;

            if (age < minAge) {
                setFieldErrors((prev) => ({
                    ...prev,
                    birthdate: t(
                        "registerErrors.minimumAge",
                        { age: minAge }
                    ),
                }));
            } else {
                setFieldErrors((prev) => ({
                    ...prev,
                    birthdate: "",
                }));
            }
        }

        // verifying phone

        if (name === "phone") {
            if (
                value &&
                !isValidPhoneNumber(value)
            ) {

                setFieldErrors(prev => ({
                    ...prev,
                    phone: t("registerErrors.invalidPhone")
                }));

            } else {

                setFieldErrors(prev => ({
                    ...prev,
                    phone: ""
                }));

            }
        }

        // validation PDF
        if (name === "national_id_pdf" && files?.[0]) {
            const file = files[0];

            if (file.type !== "application/pdf") {
                setFieldErrors((prev) => ({
                    ...prev,
                    national_id_pdf:
                        t("registerErrors.uploadPdf"),
                }));

                setFormData((prev) => ({
                    ...prev,
                    national_id_pdf: null,
                }));

                return;
            }

            setFieldErrors((prev) => ({
                ...prev,
                national_id_pdf: "",
            }));
        }
    };

    const handleDetailsChange = (e) => {
        const { name, value } = e.target;
        if (!name) return;

        setDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };


    const resetFields = (setter, keep = []) => {
        setter((prev) => {
            const next = { ...prev };

            Object.keys(next).forEach((key) => {
                if (!keep.includes(key)) {
                    next[key] = null;
                }
            });

            return next;
        });
    };

    const resetRegister = () => {
        setFormData({ language: "fr" });
        setDetails({});
        setVerificationData({});
        // setCountry("");
        setLeagues([]);
        setTeams([]);
        setLeague("");
        setLegalForm("");
        setMediaType("");

        setFieldErrors({
            username: "",
            email: "",
            birthdate: "",
            phone: "",
            password: "",
        });

        setPasswordStrength({
            score: 0,
            message: "",
            valid: false,
        });
    };

    const resetProfile = (keep = []) => {
        resetFields(setFormData, keep);
    };

    const resetDetails = (keep = []) => {
        resetFields(setDetails, keep);
    };


    const validateVerificationFiles = () => {
        const hasFile = (name) => verificationData[name] instanceof File;
        const fail = () => {
            setVerificationFilesError(true);
            toast.error(t("registerErrors.uploadRequiredFiles"));
            return false;
        };
        setVerificationFilesError(false);

        if (profile === "athlete") {
            const isProfessional = Boolean(details.level && details.level !== "Amateur");
            const isMinor = formData.birthdate && getAge(formData.birthdate) < 18;

            if (isProfessional) return hasFile("own_id") && hasFile("professional_card") ? true : fail();
            if (details.level === "Amateur" && isMinor) {
                return verificationData.hasOwnId === true
                    ? (hasFile("own_id") ? true : fail())
                    : verificationData.hasOwnId === false && hasFile("parent_id") ? true : fail();
            }
            return hasFile("own_id") ? true : fail();
        }

        if (profile === "club") {
            return details.legal_form === "societe"
                ? (hasFile("club_rc") ? true : fail())
                : hasFile("club_statuts") ? true : fail();
        }

        if (profile === "staff") {
            const needsLicence = ["staffForm.coach", "staffForm.referee"].includes(details.status);
            const needsCertificate = ["staffForm.fitnessCoach", "staffForm.scout", "staffForm.videoAnalyst", "staffForm.physio", "staffForm.nutritionist", "staffForm.mentalCoach"].includes(details.status);
            return hasFile("own_id") && (!needsLicence || hasFile("staff_license")) && (!needsCertificate || hasFile("staff_certificate")) ? true : fail();
        }

        if (profile === "lawyer") {
            const statuses = details.statuses || [];
            const agentStatuses = ["lawyerStatus.fifaAgent", "lawyerStatus.faAgent", "lawyerStatus.fffAgent", "lawyerStatus.frmfAgent", "lawyerStatus.figcAgent", "lawyerStatus.rfefAgent", "lawyerStatus.matchAgent", "lawyerStatus.sportsIntermediary"];
            const legalStatuses = ["lawyerStatus.sportsLawyer", "lawyerStatus.sportsJurist", "lawyerStatus.legalAdvisor"];
            const agentFilesPresent = statuses.filter((status) => agentStatuses.includes(status)).every((_, index) => hasFile(`agent_license_${index + 1}`));
            const hasLegalCertificate = !statuses.some((status) => legalStatuses.includes(status)) || hasFile("legal_certificate");
            const hasRepresentationCertificate = !statuses.some((status) => !agentStatuses.includes(status) && !legalStatuses.includes(status)) || hasFile("representation_certificate");
            return hasFile("own_id") && agentFilesPresent && hasLegalCertificate && hasRepresentationCertificate ? true : fail();
        }

        if (profile === "media") {
            if (details.type === "mediaForm.company") return hasFile("media_rc") ? true : fail();
            const proof = details.status === "mediaForm.journalist" ? "media_press_card" : "media_proof";
            return hasFile("own_id") && hasFile(proof) ? true : fail();
        }

        if (profile === "brand") {
            const commercial = ["legal.llc", "legal.ltd", "legal.inc", "legal.corp", "legal.sarl", "legal.sa", "legal.sas", "legal.holding", "legal.partnership"].includes(details.legal_form);
            const institutional = ["legal.association", "legal.foundation", "legal.institution"].includes(details.legal_form);
            return commercial ? (hasFile("brand_rc") ? true : fail()) : institutional ? (hasFile("brand_statuts") ? true : fail()) : hasFile("own_id") && hasFile("brand_registration") ? true : fail();
        }

        return profile !== "fan" || hasFile("own_id") ? true : fail();
    };

    useEffect(() => {
        setVerificationFilesError(false);
    }, [verificationData]);

    // send verification
    const sendVerificationCode = async () => {
        if (!validateVerificationFiles()) return;
        try {
            setLoading(true);

            await post("users/send-email-code", {
                email: formData.email,
                language: formData.language || "fr",
                username: formData.username
            });

            setEmailVerificationOpen(true);
            setLoading(false);

        } catch {
            setLoading(false);

            toast.error(
                t("registerErrors.sendCode")
            );
        }
    };

    // code verification
    const verifyEmailCode = async () => {
        try {
            setPendingRegister(true);
            setVerificationError("");

            const res = await post(
                "users/verify-email-code",
                {
                    email: formData.email,
                    code: emailCode,
                }
            );

            const message =
                res?.message || res?.data?.message;

            if (message !== "Code valide") {
                setVerificationError(
                    t("registerErrors.invalidCode")
                );
                setPendingRegister(false);
                return;
            }

            // seulement si code correct
            setEmailVerificationOpen(false);

            await handleRegisterSubmit();

        } catch (err) {
            setVerificationError(
                t("registerErrors.invalidCode")
            );
        } finally {
            setPendingRegister(false);
        }
    };


    const handleRegisterSubmit = async () => {
        if (!validateVerificationFiles()) return;

        setLoading(true);

        setFieldErrors((prev) => ({
            ...prev,
            username: "",
            email: "",
        }));

        // AGE CHECK
        if (profile === "athlete") {
            const age = getAge(formData.birthdate);

            if (!formData.birthdate || age < 13) {
                setFieldErrors((prev) => ({
                    ...prev,
                    birthdate: t(
                        "registerErrors.minimumAthleteAge"
                    ),
                }));

                setLoading(false);
                return;
            }
        }



        // nouveau pour ceux qui ont pas ID ...
        if (profile === "athlete") {
            const level = details.level;
            const age = getAge(formData.birthdate);

            const isAmateur = level === "Amateur";

            const isProfessional = Boolean(level && level !== "Amateur");

            if (isAmateur && age < 18) {
                if (verificationData.hasOwnId === true && !verificationData.own_id) {
                    toast.error(
                        t("registerErrors.addOwnId")
                    );
                    setLoading(false);
                    return;
                }

                if (
                    verificationData.hasOwnId === false &&
                    !verificationData.parent_id
                ) {
                    toast.error(
                        t("registerErrors.addParentId")
                    );
                    setLoading(false);
                    return;
                }
            }

            if (isAmateur && age >= 18 && !verificationData.own_id) {
                toast.error(
                    t("registerErrors.identityRequired")
                );
                setLoading(false);
                return;
            }

            if (
                isProfessional &&
                (
                    !verificationData.own_id ||
                    !verificationData.professional_card
                )
            ) {
                toast.error(
                    t("registerErrors.proCardRequired")
                );
                setLoading(false);
                return;
            }
        }


        if (profile === "staff") {
            const status = details.status;

            const needsLicence =
                status === "staffForm.coach" ||
                status === "staffForm.referee";

            const needsCertificate =
                [
                    "staffForm.fitnessCoach",
                    "staffForm.scout",
                    "staffForm.videoAnalyst",
                    "staffForm.physio",
                    "staffForm.nutritionist",
                    "staffForm.mentalCoach",
                ].includes(status);

            if (!verificationData.own_id) {
                toast.error(
                    t("registerErrors.identityRequired")
                );
                setLoading(false);
                return;
            }

            if (
                needsLicence &&
                !verificationData.staff_license
            ) {
                toast.error(
                    t("registerErrors.licenseRequired")
                );
                setLoading(false);
                return;
            }

            if (
                needsCertificate &&
                !verificationData.staff_certificate
            ) {
                toast.error(
                    t("registerErrors.certificateRequired")
                );
                setLoading(false);
                return;
            }
        }

        if (profile === "lawyer") {
            if (!verificationData.own_id) {
                toast.error(
                    t("registerErrors.identityRequired")
                );
                setLoading(false);
                return;
            }
        }

        if (
            profile === "lawyer" &&
            (!details.statuses || details.statuses.length === 0)
        ) {
            toast.error(
                t("registerErrors.selectSpeciality")
            );
            setLoading(false);
            return;
        }

        if (profile === "media") {
            if (details.type === "mediaForm.professional") {
                if (!verificationData.own_id) {
                    toast.error(
                        t("registerErrors.identityRequired")
                    );
                    setLoading(false);
                    return;
                }
            }
        }

        if (profile === "brand") {
            const form = details.legal_form;

            if (!form) {
                toast.error(
                    t("registerErrors.chooseLegalForm")
                );
                setLoading(false);
                return;
            }
        }

        if (
            profile === "fan" &&
            !verificationData?.own_id
        ) {
            toast.error(
                t("registerErrors.identityRequired")
            );
            setLoading(false);
            return;
        }

        try {
            const data = new FormData();

            data.append(
                "verification",
                JSON.stringify({
                    hasOwnId: verificationData.hasOwnId ?? null,
                })
            );

            // champs simples
            data.append("username", formData.username || "");
            data.append("email", formData.email || "");
            data.append("password", formData.password || "");
            data.append("language", formData.language || "fr");
            data.append("role", profile || "");

            // profile JSON
            data.append(
                "profile",
                JSON.stringify({
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    birthdate: formData.birthdate,
                    address: formData.address,
                    postal_code: formData.postal_code,
                    phone: formData.phone,
                    country: formData.country,
                })
            );

            // details JSON
            data.append(
                "details",
                JSON.stringify(details)
            );

            Object.entries(verificationData).forEach(([key, value]) => {
                if (value instanceof File) {
                    data.append(key, value);
                }
            });

            const response = await post("users", data);

            const message =
                response?.message ||
                response?.data?.message;

            if (message === "Username already exists") {
                setFieldErrors((prev) => ({
                    ...prev,
                    username:
                        t("registerErrors.usernameUsed")
                }));

                setLoading(false);
                return;
            }

            if (
                message === "Email déjà utilisé" ||
                message === "Email already exists"
            ) {
                setFieldErrors((prev) => ({
                    ...prev,
                    email:
                        t("registerErrors.emailUsed")
                }));

                setLoading(false);
                return;
            }

            setLoading(false);
            setShowSuccessModal(true);

        } catch (error) {
            setLoading(false);
            toast.error(
                t("registerErrors.registrationError")
            );
        }
    };

    useEffect(() => {
        setStep(1);
        setProfile("");
        setFormData({ language: "fr" });
        setDetails({});
        setVerificationData({});
    }, []);




    return (
        <RegisterContext.Provider value={{
            step, profile, setProfile,
            formData, details,
            handleChange, handleDetailsChange,
            next, prev, handleRegisterSubmit, fieldErrors,
            showSuccessModal, setShowSuccessModal,
            setSport, sport, setDetails, countries,
            setFootballCountry, leagues, teams, footballCountry, setLeagues,
            setLeague, setTeams, legalForm, setLegalForm, loading,
            setMediaType, mediaType, resetProfile, resetDetails, verificationData,
            setVerificationData, PHONE_RULES, passwordRules, passwordStrength, setPasswordStrength,
            verificationFilesError,
            setFieldErrors, setFormData, resetRegister, sendVerificationCode, validateVerificationFiles,
            emailVerificationOpen,
            setEmailVerificationOpen,
            emailCode,
            setEmailCode,
            verificationError,
            pendingRegister,
            verifyEmailCode, setVerificationError, getCountryTranslation

        }}>
            {children}
        </RegisterContext.Provider>
    );
};

export const useRegister = () => useContext(RegisterContext);
